import { getFreezes } from '../api/atlast';

// src/utils/dealExtras.js
//
// Shared between Dashboard.js and DealScreen.js so the ribbon a user
// sees on a trip card in the list is the exact same one they see when
// they click into it — computed once here rather than twice, the same
// principle DealScreen.js's getLinkParams comment already calls out for
// flight deep links.

// --- Shared historical price stats ------------------------------------
//
// Feeds two things (Aug 16, per Ali's report — design items 6 and 7):
// DealScreen's "usually ~$X, today $Y" comparison line, and Dashboard's
// honest urgency badges ("lowest we've tracked", "down X% since
// yesterday"). One function instead of two, so both features agree on
// what "typical" and "lowest" mean rather than each computing it
// slightly differently.
//
// Takes either shape of entry FlyAtlast already has lying around:
// price_check_log points (DealScreen, via getPinPricePoints — the full,
// uncapped history) or a pin's own capped price_history sparkline
// (Dashboard — already present on every pin from the list fetch, no
// extra API call needed per card). Both carry the same two fields this
// needs: `price_converted` (only ever set server-side when it's
// actually trustworthy — see price_checker.py's own trust-rule
// comments) and `checked_at`. Entries missing price_converted are
// dropped rather than guessed at, same "don't trust an unconverted
// comparison" rule used everywhere else in this codebase — this
// matters most for price_history, since older entries logged before
// Aug 16's currency fix have no price_converted at all and will
// correctly disappear from these stats rather than skew them.
//
// Returns null if there's nothing trustworthy to compute from at all,
// rather than a stats object full of misleading zeros.
export function computeRoutePriceStats(entries) {
  const resolved = (entries || [])
    .filter(e => e.price_converted != null)
    .map(e => ({ t: new Date(e.checked_at).getTime(), price: e.price_converted }))
    .sort((a, b) => a.t - b.t);

  if (resolved.length === 0) return null;

  const prices = resolved.map(p => p.price);
  const typical = prices.reduce((s, p) => s + p, 0) / prices.length;
  const lowest = Math.min(...prices);

  // "Price ~24h ago" — the nearest real check to exactly one day back,
  // only if history actually reaches that far. Never interpolated or
  // estimated; a pin younger than 24h just doesn't get this stat,
  // which is the honest answer, not a guess dressed up as one.
  const dayAgoTarget = Date.now() - 24 * 60 * 60 * 1000;
  let price24hAgo = null;
  if (resolved[0].t <= dayAgoTarget) {
    let closest = resolved[0];
    for (const p of resolved) {
      if (Math.abs(p.t - dayAgoTarget) < Math.abs(closest.t - dayAgoTarget)) closest = p;
    }
    price24hAgo = closest.price;
  }

  return { typical, lowest, price24hAgo, count: resolved.length };
}

// --- Ribbon selection ------------------------------------------------
//
// Exactly one ribbon per card (the single strongest signal), playful in
// tone, colored by category rather than one flat color. Priority order
// below — first match wins. All fields read here already exist on
// trip_pins (budget, last_price, price_history, check_count) — nothing
// invented.
//
// Tuning the exact thresholds (15% under budget, 25% over budget for
// "So Close" vs. further for "Slipping out", 5% price jump, 150 checks)
// is a judgment call, not a measured product decision — revisit once
// there's real usage data on what actually feels "rare"/"close"/
// "heating up" to users.
//
const SO_CLOSE_MAX_PCT_OVER = 25;
// Exported so Dashboard.js's inline "So close" budget-suggestion chip
// uses the exact same threshold number as the ribbon above — even
// though the chip computes its own over-budget percentage from
// comparablePrice() (currency-aware) rather than this file's simpler
// raw last_price/budget check, both should agree on what counts as
// "close enough to suggest."
export { SO_CLOSE_MAX_PCT_OVER };

export function pickRibbon(pin) {
  const price = pin.last_price;
  const budget = pin.budget;
  const history = pin.price_history || [];

  // Honest urgency badges (Aug 16, per Ali's report — design item 7).
  // Both real, both computed from this pin's own actual check history
  // via computeRoutePriceStats — never a fabricated number like "3
  // people are looking at this" (explicitly ruled out during the
  // design review: FlyAtlast has no real concurrent-viewer data, and
  // simulating one would be a real breach of the honesty standard the
  // rest of this app has been built on — the currency fixes, the
  // "Locked in" → "Frozen at" wording, "we don't predict, only show
  // real data"). If the data doesn't clearly support one of these,
  // neither fires — falls through to the existing Rare Find/So Close/
  // etc. checks below instead of forcing a signal that isn't there.
  const stats = computeRoutePriceStats(history);
  if (stats && price != null) {
    // "Lowest we've tracked" — checked first, since a genuine historic
    // low is a stronger, more specific claim than merely being under
    // budget. Requires at least 5 logged checks before this can fire
    // at all — with only 1-2 data points, "lowest ever" is trivially
    // true of literally every price and would be a meaningless claim
    // dressed up as insight.
    const currentResolved = pin.last_price_currency === pin.currency ? pin.last_price : pin.last_price_converted;
    if (stats.count >= 5 && currentResolved != null && currentResolved <= stats.lowest + 0.01) {
      return { label: 'New low', color: '#1E3A5F', textColor: '#F4C11C', icon: '📉' };
    }

    // "Down X%" — only fires with a real 24h-old data point to compare
    // against (never interpolated), and only above a 3% floor so
    // ordinary check-to-check noise doesn't read as a real drop. Label
    // kept to just the number, not "since yesterday" — this corner
    // ribbon is a fixed 110px rotated banner sized for short labels
    // like the existing "Rare find"/"So Close", not a full sentence.
    if (stats.price24hAgo != null && currentResolved != null) {
      const pctDown = ((stats.price24hAgo - currentResolved) / stats.price24hAgo) * 100;
      if (pctDown >= 3) {
        return { label: `Down ${pctDown.toFixed(0)}%`, color: '#1E3A5F', textColor: '#F4C11C', icon: '📉' };
      }
    }
  }

  if (price != null && budget) {
    const pctUnderBudget = ((budget - price) / budget) * 100;

    if (pctUnderBudget >= 15) {
      // Scarcity-color fix (Aug 16, per Ali's report): this used to
      // share the same green as "under budget" text and freeze
      // checkmarks — one color meaning both "safe and good" and "act
      // now, this won't last" blurred two opposite emotional
      // registers together. Rust matches the color this same
      // "scarcity" job already does on the website, moving the app and
      // site toward one shared palette instead of introducing a third
      // hue just for this. The other ribbons below (So Close, Slipping
      // out of budget, Price is heating up, Been keeping an eye out)
      // already have their own distinct colors and don't need this
      // change — only Rare find was actually sharing green with the
      // savings/success meaning.
      // Renamed 'Rare find' -> 'Good value' (Sep 18, per Ali's report —
      // real inconsistency: unlike its siblings above (New low, Down
      // X%), this fired purely on being 15%+ under the user's own
      // typed-in budget, with zero connection to real price history —
      // "rare" was never actually true of it, just "comfortably under
      // whatever number you set." Renamed to describe what it actually
      // measures rather than overclaiming rarity it never verified.
      return { label: 'Good value', color: '#1E3A5F', textColor: '#F4C11C', icon: '✨' };
    }

    if (price > budget) {
      const pctOverBudget = ((price - budget) / budget) * 100;
      if (pctOverBudget <= SO_CLOSE_MAX_PCT_OVER) {
        return {
          label: 'So Close', color: '#F4C11C',
          textColor: '#1E3A5F', icon: '🎯'
        };
      }
      return { label: 'Slipping out of budget', color: '#B23B3B', textColor: '#ffffff', icon: '⚠️' };
    }
  }

  if (history.length >= 2) {
    const prev = history[history.length - 2]?.price;
    const curr = history[history.length - 1]?.price;
    if (prev && curr && curr > prev * 1.05) {
      return { label: 'Price is heating up', color: '#C77817', textColor: '#ffffff', icon: '🔥' };
    }
  }

  if ((pin.check_count || 0) >= 150) {
    return { label: 'Been keeping an eye out', color: '#2E6FBA', textColor: '#ffffff', icon: '👀' };
  }

  return null;
}

// --- Cross-sell link builders -----------------------------------------
//
// No live pricing behind any of these (see hotel_suggestions.py's
// history — Duffel Stays access-gated, Travelpayouts/Hotellook
// discontinued Oct 2025) — every card shows action copy only
// ("Compare hotels", "Find a rental car"), never a fabricated number.
//
// PLACEHOLDER LINKS: car rental, insurance, things-to-do, airport
// transfer, and eSIM have no affiliate account behind them yet (only
// hotels does, via the Booking.com deep link already built). These use
// each provider's known public search URL as a reasonable best-effort
// destination — NOT verified affiliate deep links. Before going live,
// replace these with real affiliate URLs once those partnerships exist,
// and test each one manually the way the Booking.com flights
// currency_code param bug taught us to (a link that "looks right" isn't
// the same as a link that's been clicked and checked).

export function buildHotelSearchUrl(pin) {
  const params = new URLSearchParams();
  params.set('ss', pin.destination);
  const checkIn = pin.matched_departure_date || pin.departure_date;
  const checkOut = pin.matched_return_date || pin.return_date;
  if (checkIn) params.set('checkin', checkIn);
  if (checkOut) params.set('checkout', checkOut);
  params.set('group_adults', pin.adults || 1);
  const childAges = pin.child_ages || [];
  if (childAges.length > 0) {
    params.set('group_children', childAges.length);
    childAges.forEach((age, i) => params.set(`age[${i}]`, age));
  }
  params.set('no_rooms', 1);
  return `https://www.booking.com/searchresults.html?${params.toString()}`;
}

// The four functions below (car rental, insurance, airport transfer,
// eSIM) originally guessed at each vendor's specific search-URL format
// — Kayak's /cars/{location}, World Nomads' static page, Booking.com
// Taxi, and Airalo's search page. All four were manually click-tested
// live and confirmed dead (2026-07-25) — same mistake pattern as the
// earlier wrong Skyscanner date format, just three vendors at once.
//
// Rather than guess a third time at exact vendor URL schemes, these now
// use a plain Google Search query — the same fallback technique already
// proven reliable elsewhere in this file (see handleLookup in
// DealScreen.js for the flight-lookup equivalent). It never 404s and
// always returns real, useful results; it's just an honest general
// search rather than a guaranteed vendor-specific deep link. Revisit
// with a real vendor deep link once one is actually verified working.

export function buildCarRentalUrl(pin) {
  return `https://www.google.com/search?q=${encodeURIComponent(`car rental in ${pin.destination}`)}`;
}

export function buildInsuranceUrl(pin) {
  return `https://www.google.com/search?q=${encodeURIComponent(`travel insurance for a trip to ${pin.destination}`)}`;
}

export function buildThingsToDoUrl(pin) {
  // PLACEHOLDER — Viator's public text-search results page. Confirmed
  // still working as of 2026-07-25 (not crossed out in live testing).
  return `https://www.viator.com/searchResults/all?text=${encodeURIComponent(pin.destination)}`;
}

export function buildAirportTransferUrl(pin) {
  return `https://www.google.com/search?q=${encodeURIComponent(`airport transfer in ${pin.destination}`)}`;
}

export function buildEsimUrl(pin) {
  return `https://www.google.com/search?q=${encodeURIComponent(`esim for ${pin.destination}`)}`;
}

export function buildPackingChecklistUrl() {
  // Non-affiliate, content-only — no commission behind this one on
  // purpose, kept for engagement rather than revenue.
  return 'https://www.nomadicmatt.com/travel-blogs/ultimate-packing-list/';
}

// Shared between Dashboard.js's "Found Sep 15" pin-card badge and
// DealScreen.js's freshness indicator (Sep 15, per Ali's report — real
// inconsistency, flagged directly from a screenshot: both read from the
// exact same pin.last_checked timestamp, but Dashboard showed only a
// date while DealScreen showed only a time, with no date at all — so a
// check from days ago looked identical to one from five minutes ago on
// DealScreen, and the two screens looked like they were describing two
// different events for the same pin). DealScreen has the room to show
// full date + time; Dashboard's compact card stub doesn't, so it keeps
// its own shorter date-only rendering — but that date-only string is
// now a genuine substring of what this returns, so the two screens
// visibly agree rather than looking unrelated.
export function formatCheckedWhen(timestamp) {
  const d = new Date(timestamp);
  const now = new Date();
  const timeStr = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (d.toDateString() === now.toDateString()) {
    return `today at ${timeStr}`;
  }
  const dateStr = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${dateStr} at ${timeStr}`;
}

// New (Sep 18, per Ali's report — items 4/5/6 of the Sep 17 QA doc):
// every raw price across the app was being interpolated straight into
// JSX with no formatting at all — no thousands separator, and an
// inconsistent number of decimals (sometimes one, like "2799.6",
// sometimes none, like "2593"). No shared formatter existed anywhere
// for this; every screen was just doing `{price} {currency}` directly.
// Always exactly 2 decimals, comma thousands separator — the one
// place this needs to format a currency's own symbol/style
// differently (not just en-US-style commas) can still do that itself;
// this only owns the number formatting, not the currency code/symbol
// placement.
export function formatMoney(amount) {
  if (amount == null || isNaN(amount)) return amount;
  return Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Brand/consistency fix (Oct 2026): show the city, not the airport name, in
// user-facing titles. Pins only store the airport name, so this strips the
// generic airport words ("Luxembourg Airport" -> "Luxembourg"). If the pin ever
// stores a real city name, pass that instead.
export function cityFromAirportName(name) {
  if (!name || typeof name !== 'string') return name;
  const cleaned = name
    .replace(/\b(International|Intl\.?|Int'l|Regional|Municipal|Domestic)\b/gi, '')
    .replace(/\b(Airport|Airfield|Aerodrome|Air Base)\b/gi, '')
    .replace(/\s{2,}/g, ' ')
    .replace(/[\s,\-–—]+$/g, '')
    .trim();
  return (cleaned || name).replace(/\s+,/g, ',');
}

// Oct 13: card/page title for a pin, "City (CODE), Country" (e.g. "Oslo (OSL),
// Norway"). The backend adds destination_label; older responses or failed
// lookups fall back to the cleaned airport name.
export function pinTitle(pin) {
  if (!pin) return '';
  if (pin.pin_type === 'hotel') return pin.location;
  return pin.destination_label || cityFromAirportName(pin.destination);
}

// Same, city only, for use inside sentences ("At last! Oslo is a deal").
export function pinCity(pin) {
  const t = pinTitle(pin);
  return typeof t === 'string' ? t.split(' (')[0] : t;
}

// Budgets are usually whole numbers: show thousands separators without forcing decimals.
export function formatBudget(amount) {
  if (amount == null || isNaN(amount)) return amount;
  const n = Number(amount);
  return Number.isInteger(n) ? n.toLocaleString('en-US') : formatMoney(n);
}

// Server error text may still use the retired "freeze" wording; show users "watch".
export function friendlyDetail(detail) {
  if (!detail || typeof detail !== 'string') return detail;
  return detail
    .replace(/\bPrice Freeze(s)?\b/g, (m, pl) => (pl ? 'Price Watches' : 'Price Watch'))
    .replace(/\bfreezes\b/gi, 'watches')
    .replace(/\bfreeze\b/gi, 'watch');
}

// When starting a Price Watch fails WITHOUT any answer from the server (a slow or
// dropped phone connection while the ~10 second request was running), the watch
// may well have been created anyway. Before showing an error, look for a watch
// on this pin made since the attempt began. Returns it, or null.
export async function recoverCreatedFreeze(pinId, userId, startedAtMs) {
  try {
    const res = await getFreezes(userId);
    const list = (res && res.freezes) || [];
    return list.find((f) => f.pin_id === pinId && new Date(f.created_at).getTime() >= startedAtMs - 60000) || null;
  } catch (e) {
    return null;
  }
}
